import katex from 'katex';

/**
 * Parse text and identify LaTeX formula segments
 * @param {string} text - Input text containing potential LaTeX formulas
 * @returns {Array} Array of segments with type and content
 */
export function parseTextWithFormulas(text) {
  if (!text) return [];

  const segments = [];
  const formulaRegex = /\$([^$]+)\$/g;
  let lastIndex = 0;
  let match;

  while ((match = formulaRegex.exec(text)) !== null) {
    // Add text before formula
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.substring(lastIndex, match.index),
      });
    }

    // Add formula segment
    segments.push({
      type: 'formula',
      content: match[1], // LaTeX content without dollar signs
      raw: match[0], // Full formula with dollar signs
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last formula
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(lastIndex),
    });
  }

  return segments;
}

/**
 * Render a LaTeX formula using KaTeX
 * @param {string} latex - LaTeX formula string (without dollar signs)
 * @returns {string} Rendered HTML string
 */
export function renderFormula(latex) {
  try {
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode: false, // Inline mode for chat input
      output: 'html',
      strict: false,
      trust: true,
      macros: {
        // Add any custom macros if needed
      },
    });
  } catch (error) {
    console.error('Error rendering formula:', error);
    // Return the original LaTeX if rendering fails
    return `$${latex}$`;
  }
}

/**
 * Convert contentEditable HTML back to plain text with LaTeX notation
 * @param {HTMLElement} element - The contentEditable element
 * @returns {string} Plain text with LaTeX formulas
 */
export function extractTextWithFormulas(element) {
  if (!element) return '';

  let text = '';

  // Process direct children and their descendants
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Only add text if it's not inside a formula span
      let parent = node.parentElement;
      let isInsideFormula = false;

      while (parent && parent !== element) {
        if (parent.classList && parent.classList.contains('chat-input__formula')) {
          isInsideFormula = true;
          break;
        }
        parent = parent.parentElement;
      }

      if (!isInsideFormula) {
        text += node.textContent;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.classList && node.classList.contains('chat-input__formula')) {
        // Extract the original LaTeX from data attribute
        const latex = node.getAttribute('data-latex');
        if (latex) {
          text += `$${latex}$`;
        }
        // Don't process children of formula spans
        return;
      } else if (node.tagName === 'BR') {
        text += '\n';
      } else {
        // Process children recursively
        for (const child of node.childNodes) {
          processNode(child);
        }
      }
    }
  };

  // Start processing from the root element's children
  for (const child of element.childNodes) {
    processNode(child);
  }

  return text;
}

/**
 * Create HTML content for contentEditable with rendered formulas
 * @param {string} text - Plain text with LaTeX formulas
 * @returns {string} HTML string with rendered formulas
 */
export function createRichContent(text) {
  const segments = parseTextWithFormulas(text);

  return segments
    .map((segment) => {
      if (segment.type === 'text') {
        // Escape HTML and preserve whitespace
        const escaped = segment.content
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
          .replace(/\n/g, '<br>')
          .replace(/ {2}/g, '&nbsp;&nbsp;');
        return escaped || '';
      } else if (segment.type === 'formula') {
        const rendered = renderFormula(segment.content);
        // Wrap in a span with the original LaTeX as a data attribute
        return `<span class="chat-input__formula" data-latex="${segment.content}" contenteditable="false">${rendered}</span>`;
      }
      return '';
    })
    .join('');
}

/**
 * Get the current cursor position in plain text (accounting for formulas)
 * @param {HTMLElement} element - The contentEditable element
 * @returns {number} Cursor position in plain text
 */
export function getCursorPosition(element) {
  const selection = window.getSelection();
  if (!selection.rangeCount) return 0;

  const range = selection.getRangeAt(0);
  let position = 0;

  // Walk through nodes to calculate position
  const countPosition = (node, stopNode, stopOffset) => {
    if (node === stopNode) {
      if (node.nodeType === Node.TEXT_NODE) {
        // Only count text if it's not inside a formula
        let parent = node.parentElement;
        let isInsideFormula = false;

        while (parent && parent !== element) {
          if (parent.classList && parent.classList.contains('chat-input__formula')) {
            isInsideFormula = true;
            break;
          }
          parent = parent.parentElement;
        }

        if (!isInsideFormula) {
          position += Math.min(stopOffset, node.textContent.length);
        }
      }
      return true; // Found the target
    }

    if (node.nodeType === Node.TEXT_NODE) {
      // Only count text if it's not inside a formula
      let parent = node.parentElement;
      let isInsideFormula = false;

      while (parent && parent !== element) {
        if (parent.classList && parent.classList.contains('chat-input__formula')) {
          isInsideFormula = true;
          break;
        }
        parent = parent.parentElement;
      }

      if (!isInsideFormula) {
        position += node.textContent.length;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.classList && node.classList.contains('chat-input__formula')) {
        const latex = node.getAttribute('data-latex');
        if (latex) {
          position += latex.length + 2; // +2 for dollar signs
        }
        return false; // Don't process children
      } else if (node.tagName === 'BR') {
        position += 1; // Count as newline
      } else {
        // Process children
        for (const child of node.childNodes) {
          if (countPosition(child, stopNode, stopOffset)) {
            return true;
          }
        }
      }
    }

    return false;
  };

  // Count position from the beginning
  for (const child of element.childNodes) {
    if (countPosition(child, range.endContainer, range.endOffset)) {
      break;
    }
  }

  return position;
}

/**
 * Set cursor position in contentEditable element
 * @param {HTMLElement} element - The contentEditable element
 * @param {number} position - Desired cursor position in plain text
 */
export function setCursorPosition(element, position) {
  const text = extractTextWithFormulas(element);
  if (position > text.length) position = text.length;
  if (position < 0) position = 0;

  const selection = window.getSelection();
  const range = document.createRange();

  let currentPos = 0;
  let found = false;

  // Find the target position
  const findPosition = (node) => {
    if (found) return;

    if (node.nodeType === Node.TEXT_NODE) {
      // Check if this text node is inside a formula
      let parent = node.parentElement;
      let isInsideFormula = false;

      while (parent && parent !== element) {
        if (parent.classList && parent.classList.contains('chat-input__formula')) {
          isInsideFormula = true;
          break;
        }
        parent = parent.parentElement;
      }

      if (!isInsideFormula) {
        const nodeLength = node.textContent.length;
        if (currentPos + nodeLength >= position) {
          // Found the target position
          const offset = position - currentPos;
          range.setStart(node, Math.min(offset, node.length));
          range.setEnd(node, Math.min(offset, node.length));
          found = true;
        } else {
          currentPos += nodeLength;
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.classList && node.classList.contains('chat-input__formula')) {
        const latex = node.getAttribute('data-latex');
        const formulaLength = latex ? latex.length + 2 : 0; // +2 for dollar signs

        if (currentPos + formulaLength >= position) {
          // Position should be right after the formula
          if (node.nextSibling) {
            if (node.nextSibling.nodeType === Node.TEXT_NODE) {
              range.setStart(node.nextSibling, 0);
              range.setEnd(node.nextSibling, 0);
            } else {
              range.setStartAfter(node);
              range.setEndAfter(node);
            }
          } else {
            // No next sibling, position at the end of parent
            range.setStartAfter(node);
            range.setEndAfter(node);
          }
          found = true;
        } else {
          currentPos += formulaLength;
        }
      } else if (node.tagName === 'BR') {
        currentPos += 1;
        if (currentPos >= position) {
          range.setStartBefore(node);
          range.setEndBefore(node);
          found = true;
        }
      } else {
        // Process children
        for (const child of node.childNodes) {
          findPosition(child);
          if (found) break;
        }
      }
    }
  };

  // Start searching from the root
  for (const child of element.childNodes) {
    findPosition(child);
    if (found) break;
  }

  // If position not found, place cursor at the end
  if (!found) {
    range.selectNodeContents(element);
    range.collapse(false);
  }

  try {
    selection.removeAllRanges();
    selection.addRange(range);
  } catch (error) {
    console.error('Error setting cursor position:', error);
    // Fallback: position at the end
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}
